import React, { useState, useMemo, useEffect } from 'react';
import { Button, Box, Typography, Autocomplete, TextField } from '@mui/material';
import { Plus } from 'lucide-react';
import DataTable from '../../common/DataTable/index';
import Search from '../../common/Search';
import CommonDialog from '../../common/CommonDialog';
import AddSoftware from './AddSoftware';
import { fetchSoftwareNames } from '../../../../apis/Masters/SoftwareName';
import { useResponsive, userScreenAccess } from '../../../../utils';
import ExportExcel from '../../common/ExportExcel';
import { statusOption } from '../../../../constants/constant';
import { FilterFieldSx, DropDownSX } from '../../../../constants/style';

let _editRowHandler = null;

const headers = [
  {
    label: 'Software Name',
    key: 'softwareName',
    minWidth: 150,
    sortable: true,
    align: 'left',
    resizable: true,
    render: (val, row) => row.softwareName || row.name || 'Unknown'
  },
  {
    label: 'Status',
    key: 'isActive',
    minWidth: 140,
    sortable: true,
    align: 'center',
    render: (val) => {
      const statusText = val ? 'Active' : 'Inactive';
      const badgeClass =
        {
          true: 'bg-success-subtle text-success',
          false: 'bg-danger-subtle text-danger'
        }[val] || 'bg-secondary';
      return <span className={`${badgeClass} px-2 py-1 fw-semibold`}>{statusText}</span>;
    }
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const SoftwareList = () => {
  const [softwares, setSoftwares] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusGUID, setStatusGUID] = useState(1);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSoftware, setSelectedSoftware] = useState(null); // null = Add mode
  const { isMobile } = useResponsive();
  const userAccess = userScreenAccess();

  // Wire module-level handler for the Actions column
  _editRowHandler = (row) => {
    if (!userAccess.hasEditPermission) return;
    setSelectedSoftware(row);
    setOpenDialog(true);
  };

  // Open Add dialog
  const handleAddSoftware = () => {
    if (!userAccess.hasAddPermission) return;
    setSelectedSoftware(null);
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSoftware(null);
  };

  const fetchSoftwareListResponse = async (statusId) => {
    if (statusId === undefined) statusId = statusGUID; // use current state if not provided
    setIsLoading(true);
    const response = await fetchSoftwareNames(statusId);
    setSoftwares(Array.isArray(response) ? response : []);
    setIsLoading(false);
  };

  const handleStatusChange = (event, value) => {
    const status = value?.value;
    setStatusGUID(status);
    fetchSoftwareListResponse(status);
  };

  useEffect(() => {
    fetchSoftwareListResponse(statusGUID);
  }, []);

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return softwares;
    return softwares.filter((row) => {
      // Normalize values: convert boolean isActive → display text so
      // searching "Active" or "Inactive" / "InActive" works correctly
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [searchQuery, softwares]);

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Software List
        </Typography>
      </Box>
      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <Autocomplete
          size="small"
          options={statusOption}
          value={statusOption.find((u) => u.value === statusGUID) || null}
          onChange={handleStatusChange}
          disableClearable={true}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          sx={{ width: '30%' }} // adjust width here
          slotProps={{
            popper: {
              sx: DropDownSX
            }
          }}
          renderInput={(params) => (
            <TextField {...params} sx={FilterFieldSx} placeholder="Select Status" />
          )}
        />

        <Search
          width={isMobile ? '200px' : '280px'}
          height="36px"
          placeholder="Search by software name or status..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div className="d-flex align-items-center gap-2">
          <ExportExcel
            data={softwares}
            fileName={`Software_${new Date().toISOString().slice(0, 10)}.xlsx`}
            sheetName={'Software'}
            buttonLabel="Excel"
            exportKeys={headers.filter((h) => h.key !== '_actions').map((h) => h.key)}
            exportHeaders={headers.filter((h) => h.key !== '_actions').map((h) => h.label)}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={15} />}
            onClick={handleAddSoftware}
            sx={{
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: userAccess.hasAddPermission ? 'inline-flex' : 'none'
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* ── Data Table ────────────────────────────────────────────────── */}
      <DataTable
        headers={headers}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        emptyMessage={
          searchQuery ? `No results for "${searchQuery}"` : 'No software found in the system.'
        }
        enablePagination={true}
        zebra={false}
        onRowClick={_editRowHandler}
      />

      {/* ── Add / Edit Dialog ─────────────────────────────────────────── */}
      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={
          selectedSoftware
            ? `Edit Software — ${selectedSoftware.softwareName || selectedSoftware.name}`
            : 'Add New Software'
        }
        maxWidth="sm"
        persistent={true}
        fullScreen={isMobile ? true : false}
      >
        <AddSoftware
          softwareData={selectedSoftware}
          userAccess={userAccess}
          onRefresh={fetchSoftwareListResponse}
          onClose={handleCloseDialog}
        />
      </CommonDialog>
    </div>
  );
};

export default SoftwareList;
